// The tide and the sea's state as the server last said them (the `water` message, about five times a second), and the tide eased between the messages.
//
// Human-facing only, and view-only: the client holds no water state of its own. The server's water world (water_world.py) works the tide out from its own sun and moon, so every viewer and the physics
// agree on it; the renderer uses `tide` for the sea (waterView.ts) in place of the local formula (tide.ts), which stays as the fallback for a server that sends no water message.
// The message also carries per-basin levels (standing pools); this client draws none, so they are not read.

export interface WaterStateMessage {
  type: 'water';
  tick: number; sea_level: number;
  tide: number; setup: number; swash: number; ocean: number;      // m: the tide alone, wave set-up, swash, and their sum with the sea level
  hs: number; tp: number; wind: number; rain_mm_h: number; ledger_ok: boolean;
  swell?: { hs: number; tp: number; dir: [number, number] }[];     // swell trains from far storms: height (m), period (s), the unit vector (east, north) they travel toward
}

const TIDE_TAU_S = 0.35;           // seconds the drawn tide takes to catch up with the server's (messages come about five times a second)
const SNAP_M = 0.75;               // a jump bigger than this (a restart, a new world) is taken at once

export class TideState {
  private _tide = 0;
  private target = 0;
  private have = false;
  private msg: WaterStateMessage | null = null;

  /** True once the server has sent a water state: the renderer then uses `tide` and not its own formula. */
  get hasState(): boolean { return this.have; }
  /** Tide height in metres relative to the sea level, eased between the server's messages. */
  get tide(): number { return this._tide; }
  /** The server's last water message (wave state, rain, ledger flag), or null. */
  get state(): WaterStateMessage | null { return this.msg; }

  /** The newest water state. `snap`: take it at once (a first message, or a restart). */
  setState(msg: WaterStateMessage, snap = false): void {
    this.msg = msg;
    if (!this.have || snap || Math.abs(msg.tide - this.target) > SNAP_M) this._tide = msg.tide;
    this.target = msg.tide;
    this.have = true;
  }

  /** Per frame: ease the drawn tide toward the server's. */
  update(dt: number): void {
    this._tide += (this.target - this._tide) * (1 - Math.exp(-Math.max(dt, 0) / TIDE_TAU_S));
  }

  /** Forget the state (a new connection). */
  reset(): void { this.have = false; this.msg = null; this._tide = 0; this.target = 0; }
}
